import React, { Component } from "react";
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Input,
  Label,
  FormGroup,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
} from "reactstrap";
import axios from "axios";
import { END_POINT } from "../../../constants/defaultValues";
import {
  NotificatioErrController,
  NotificatioSucController,
} from "../../../helpers/Utils";
import { injectIntl } from "react-intl";
import IntlMessages from "../../../helpers/IntlMessages";

/**
 * QuickReplySettings - Quản lý mẫu trả lời nhanh
 * Phase 2: Quick Replies & Templates
 */
class QuickReplySettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      templates: [],
      loading: true,
      showModal: false,
      editingTemplate: null,
      formData: {
        title: "",
        content: "",
        shortcut: "",
        category: "general",
      },
      saving: false,
    };
  }

  componentDidMount() {
    this.loadTemplates();
  }

  loadTemplates = async () => {
    try {
      this.setState({ loading: true });
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${END_POINT}/api/quick-replies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      this.setState({ templates: response.data.data || [], loading: false });
    } catch (err) {
      console.error("Error loading templates:", err);
      NotificatioErrController(
        this.props.intl.formatMessage({
          id: "settings.quickreply.notify.load-error",
        }),
      );
      this.setState({ loading: false });
    }
  };

  openModal = (template = null) => {
    if (template) {
      this.setState({
        showModal: true,
        editingTemplate: template,
        formData: {
          title: template.title,
          content: template.content,
          shortcut: template.shortcut || "",
          category: template.category || "general",
        },
      });
    } else {
      this.setState({
        showModal: true,
        editingTemplate: null,
        formData: {
          title: "",
          content: "",
          shortcut: "",
          category: "general",
        },
      });
    }
  };

  closeModal = () => {
    this.setState({
      showModal: false,
      editingTemplate: null,
      formData: { title: "", content: "", shortcut: "", category: "general" },
    });
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      formData: { ...prev.formData, [name]: value },
    }));
  };

  handleSave = async () => {
    const { formData, editingTemplate } = this.state;
    const { intl } = this.props;

    if (!formData.title || !formData.content) {
      NotificatioErrController(
        intl.formatMessage({ id: "settings.quickreply.notify.input-error" }),
      );
      return;
    }

    try {
      this.setState({ saving: true });
      const token = localStorage.getItem("access_token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingTemplate) {
        await axios.put(
          `${END_POINT}/api/quick-replies/${editingTemplate.id}`,
          formData,
          config,
        );
        NotificatioSucController(
          intl.formatMessage({
            id: "settings.quickreply.notify.update-success",
          }),
        );
      } else {
        await axios.post(`${END_POINT}/api/quick-replies`, formData, config);
        NotificatioSucController(
          intl.formatMessage({
            id: "settings.quickreply.notify.create-success",
          }),
        );
      }

      this.closeModal();
      this.loadTemplates();
    } catch (err) {
      console.error("Error saving template:", err);
      NotificatioErrController(
        err.response?.data?.error ||
          intl.formatMessage({ id: "settings.quickreply.notify.save-error" }),
      );
    } finally {
      this.setState({ saving: false });
    }
  };

  handleDelete = async (id) => {
    const { intl } = this.props;
    if (
      !window.confirm(
        intl.formatMessage({ id: "settings.quickreply.notify.delete-confirm" }),
      )
    )
      return;

    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${END_POINT}/api/quick-replies/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      NotificatioSucController(
        intl.formatMessage({ id: "settings.quickreply.notify.delete-success" }),
      );
      this.loadTemplates();
    } catch (err) {
      console.error("Error deleting template:", err);
      NotificatioErrController(
        intl.formatMessage({ id: "settings.quickreply.notify.delete-error" }),
      );
    }
  };

  getCategoryBadge = (category) => {
    const colors = {
      greeting: "primary",
      thanks: "success",
      faq: "info",
      promotion: "warning",
      general: "secondary",
    };
    const labels = {
      greeting: <IntlMessages id="settings.quickreply.cat.greeting" />,
      thanks: <IntlMessages id="settings.quickreply.cat.thanks" />,
      faq: <IntlMessages id="settings.quickreply.cat.faq" />,
      promotion: <IntlMessages id="settings.quickreply.cat.promotion" />,
      general: <IntlMessages id="settings.quickreply.cat.general" />,
    };
    return (
      <Badge color={colors[category] || "secondary"}>
        {labels[category] || category}
      </Badge>
    );
  };

  render() {
    const { templates, loading, showModal, formData, saving, editingTemplate } =
      this.state;
    const { intl } = this.props;

    return (
      <Card className="mb-4">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <CardTitle className="mb-0">
              <i className="simple-icon-bubbles mr-2" />
              <IntlMessages id="settings.quickreply.title" />
            </CardTitle>
            <Button color="primary" size="sm" onClick={() => this.openModal()}>
              <i className="simple-icon-plus mr-1" />
              <IntlMessages id="settings.quickreply.add" />
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center text-muted py-4">
              <i
                className="simple-icon-bubbles mb-2"
                style={{ fontSize: 40 }}
              />
              <p>
                <IntlMessages id="settings.quickreply.empty.title" />
              </p>
              <Button color="primary" onClick={() => this.openModal()}>
                <IntlMessages id="settings.quickreply.create-first" />
              </Button>
            </div>
          ) : (
            <Table responsive className="table-hover">
              <thead>
                <tr>
                  <th>
                    <IntlMessages id="settings.quickreply.col.title" />
                  </th>
                  <th>
                    <IntlMessages id="settings.quickreply.col.content" />
                  </th>
                  <th>
                    <IntlMessages id="settings.quickreply.col.shortcut" />
                  </th>
                  <th>
                    <IntlMessages id="settings.quickreply.col.category" />
                  </th>
                  <th className="text-right">
                    <IntlMessages id="settings.quickreply.col.action" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.id}>
                    <td className="font-weight-semibold">{t.title}</td>
                    <td
                      className="text-muted"
                      style={{
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t.content}
                    </td>
                    <td>{t.shortcut && <code>{t.shortcut}</code>}</td>
                    <td>{this.getCategoryBadge(t.category)}</td>
                    <td className="text-right">
                      <Button
                        color="link"
                        size="sm"
                        onClick={() => this.openModal(t)}
                      >
                        <i className="simple-icon-pencil" />
                      </Button>
                      <Button
                        color="link"
                        size="sm"
                        className="text-danger"
                        onClick={() => this.handleDelete(t.id)}
                      >
                        <i className="simple-icon-trash" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {/* Modal thêm/sửa */}
          <Modal isOpen={showModal} toggle={this.closeModal}>
            <ModalHeader toggle={this.closeModal}>
              {editingTemplate ? (
                <IntlMessages id="settings.quickreply.modal.title-edit" />
              ) : (
                <IntlMessages id="settings.quickreply.modal.title-add" />
              )}
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>
                  <IntlMessages id="settings.quickreply.modal.label.title" />
                </Label>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={this.handleInputChange}
                  placeholder={intl.formatMessage({
                    id: "settings.quickreply.modal.placeholder.title",
                  })}
                />
              </FormGroup>
              <FormGroup>
                <Label>
                  <IntlMessages id="settings.quickreply.modal.label.content" />
                </Label>
                <Input
                  type="textarea"
                  name="content"
                  value={formData.content}
                  onChange={this.handleInputChange}
                  rows={4}
                  placeholder={intl.formatMessage({
                    id: "settings.quickreply.modal.placeholder.content",
                  })}
                />
              </FormGroup>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>
                      <IntlMessages id="settings.quickreply.modal.label.shortcut" />
                    </Label>
                    <Input
                      name="shortcut"
                      value={formData.shortcut}
                      onChange={this.handleInputChange}
                      placeholder={intl.formatMessage({
                        id: "settings.quickreply.modal.placeholder.shortcut",
                      })}
                    />
                    <small className="text-muted">
                      <IntlMessages id="settings.quickreply.modal.help.shortcut" />
                    </small>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>
                      <IntlMessages id="settings.quickreply.modal.label.category" />
                    </Label>
                    <Input
                      type="select"
                      name="category"
                      value={formData.category}
                      onChange={this.handleInputChange}
                    >
                      <option value="general">
                        {intl.formatMessage({
                          id: "settings.quickreply.cat.general",
                        })}
                      </option>
                      <option value="greeting">
                        {intl.formatMessage({
                          id: "settings.quickreply.cat.greeting",
                        })}
                      </option>
                      <option value="thanks">
                        {intl.formatMessage({
                          id: "settings.quickreply.cat.thanks",
                        })}
                      </option>
                      <option value="faq">
                        {intl.formatMessage({
                          id: "settings.quickreply.cat.faq",
                        })}
                      </option>
                      <option value="promotion">
                        {intl.formatMessage({
                          id: "settings.quickreply.cat.promotion",
                        })}
                      </option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={this.closeModal}>
                <IntlMessages id="settings.quickreply.modal.cancel" />
              </Button>
              <Button
                color="primary"
                onClick={this.handleSave}
                disabled={saving}
              >
                {saving ? (
                  <span className="spinner-border spinner-border-sm mr-1" />
                ) : null}
                {editingTemplate ? (
                  <IntlMessages id="settings.quickreply.modal.update" />
                ) : (
                  <IntlMessages id="settings.quickreply.modal.save" />
                )}
              </Button>
            </ModalFooter>
          </Modal>
        </CardBody>
      </Card>
    );
  }
}

export default injectIntl(QuickReplySettings);
